import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';

import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import {
	Typography,
	List,
	ListItem,
	ListItemText,
	ListItemSecondaryAction,
	Box,
	Button,
	ListSubheader,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import Spinner from 'components/Spinner';

import { SegmentMap, Category } from 'common/types';
import * as StockSelectors from 'selectors/StockSelectors';
import * as LanguageSelector from 'selectors/LanguageSelectors';
import { useTranslation } from 'services/localization/useTranslation';
import { api } from 'common/frontend/api';
import { getTranslation } from 'common/modules/translations';

const SkidataLiftTicketSegments = () => {
	const { t } = useTranslation();
	const classes = useStyles();

	const [pendingCategorySegments, setPendingCategorySegments] = useState<SegmentMap[]>([]);
	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState('');

	const categoriesLoading = useSelector(StockSelectors.categoriesLoading);
	const liftTicketCategory = useSelector(StockSelectors.liftTicketCategory);
	const language = useSelector(LanguageSelector.language);
	const liftTicketCategorySegments = useMemo(() => liftTicketCategory?.segmentMapping ?? [], [
		liftTicketCategory?.segmentMapping,
	]);

	useEffect(() => {
		if (liftTicketCategorySegments.length > 0) {
			setPendingCategorySegments(liftTicketCategorySegments);
		}
	}, [liftTicketCategorySegments]);

	const onToggle = (segmentId: string) => {
		const updatedCategorySegments: SegmentMap[] = pendingCategorySegments.map((s) =>
			s.externalSegmentId === segmentId ? { ...s, hidden: !s.hidden } : s,
		);
		setPendingCategorySegments(updatedCategorySegments);
	};

	const handleCategorySave = async () => {
		if (!liftTicketCategory) {
			return;
		}
		setLoading(true);
		try {
			const updatedLiftTicketCategory: Category = {
				...liftTicketCategory,
				segmentMapping: pendingCategorySegments,
			};
			await api()
				.productCategories.doc(liftTicketCategory.id)
				.set(updatedLiftTicketCategory, { merge: true });
			setLoading(false);
		} catch (e) {
			setLoading(false);
			setError(e.message);
		}
	};

	const renderVisibilityBadge = (isHidden?: boolean) => (
		<Typography
			className={classNames(classes.visibilityToggle, {
				[classes.visibilityToggleHidden]: isHidden,
			})}
			variant="button"
			color={isHidden ? 'textSecondary' : 'primary'}
		>
			{isHidden ? (
				<VisibilityOff fontSize="small" style={{ color: 'inherit' }} />
			) : (
				<Visibility fontSize="small" style={{ color: 'inherit' }} />
			)}
			<Box mr={0.5} />
			<span className={classes.visibilityToggleText}>
				{isHidden ? t('common:states.hidden') : t('common:states.visible')}
			</span>
		</Typography>
	);

	const renderSegmentVisibilityItem = (segment: SegmentMap) => {
		return (
			<ListItem
				button
				divider
				key={segment.externalSegmentId}
				className={classes.listItem}
				onClick={() => onToggle(segment.externalSegmentId)}
			>
				<ListItemText
					inset
					primary={segment.externalSegmentName}
					primaryTypographyProps={{ className: classes.capitalize }}
					classes={{
						root: classes.listItemText,
						inset: classes.listItemTextInset,
					}}
				/>

				<ListItemText
					primary={segment.name && getTranslation(segment.name, language)}
					primaryTypographyProps={{ className: classes.capitalize }}
					classes={{
						root: classes.listItemText,
						inset: classes.listItemTextInset,
					}}
				/>

				<ListItemSecondaryAction className={classes.pointerEventsNone}>
					{renderVisibilityBadge(segment.hidden)}
				</ListItemSecondaryAction>
			</ListItem>
		);
	};

	return !liftTicketCategory ? null : categoriesLoading ? (
		<Spinner relative />
	) : liftTicketCategorySegments.length === 0 ? null : (
		<Box py={4}>
			<Typography variant="h6" gutterBottom>
				{t('product.discountGroups')}
			</Typography>
			<Typography variant="body2" gutterBottom>
				{t('product.integrationDescription')}
			</Typography>

			<List>
				<ListItem>
					<ListSubheader
						inset
						classes={{
							root: classes.listItemText,
							inset: classes.listItemTextInset,
						}}
					>
						{`Skidata DTA ${t('common:form.name')}`}
					</ListSubheader>
					<ListSubheader
						inset
						classes={{
							root: classes.listItemText,
							inset: classes.listItemTextInset,
						}}
					>{`Rentle ${t('account.languages.translationPlural')}`}</ListSubheader>
				</ListItem>
				{pendingCategorySegments.map(renderSegmentVisibilityItem)}
			</List>
			<Box mt={3}>
				{error && (
					<Typography variant="body1" color="error" gutterBottom>
						{error}
					</Typography>
				)}
				<Button disabled={loading} variant="contained" color="primary" onClick={handleCategorySave}>
					{loading ? <Spinner padding={8} thickness={4} /> : t('common:actions.save')}
				</Button>
			</Box>
		</Box>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		listItem: {
			padding: theme.spacing(2),
			'&:hover': {
				background: 'rgba(0,0,0,0.03)',
			},
		},
		capitalize: {
			textTransform: 'capitalize',
		},
		listItemText: {
			minWidth: '50%',
			paddingRight: '100px',
			[theme.breakpoints.down('sm')]: {
				paddingRight: 0,
			},
		},
		listItemTextInset: {
			paddingLeft: 0,
		},
		pointerEventsNone: {
			pointerEvents: 'none',
		},
		visibilityToggle: {
			display: 'flex',
			flexDirection: 'row',
			alignItems: 'center',
		},
		visibilityToggleText: {
			[theme.breakpoints.down('sm')]: {
				display: 'none',
			},
		},
		visibilityToggleHidden: {
			opacity: 0.5,
		},
	}),
);

export default SkidataLiftTicketSegments;
